/**
 * 비밀번호 입력 — 표시/숨김 토글 포함.
 */
import { Input, type InputProps } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** 토글 버튼 왼쪽에 표시 (성공 체크 등) */
  trailing?: React.ReactNode;
}

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(({ className, trailing, disabled, startIcon, ...props }, ref) => {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative w-full">
      <Input
        {...props}
        ref={ref}
        startIcon={startIcon}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        clearable={false}
        className={className}
        inputClassName={cn(trailing ? 'pr-16' : 'pr-9')}
      />
      {trailing ? (
        <span className="pointer-events-none absolute top-1/2 right-9 flex -translate-y-1/2 items-center">
          {trailing}
        </span>
      ) : null}
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded-8 p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        onClick={() => setVisible((prev) => !prev)}
        aria-label={visible ? '비밀번호 숨기기' : '비밀번호 보기'}
        aria-pressed={visible}
      >
        {visible ? (
          <EyeOff className="h-4 w-4" aria-hidden />
        ) : (
          <Eye className="h-4 w-4" aria-hidden />
        )}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';
